import { useCallback, useState } from 'react'

import { errorView, type QueryStatus, type QueryView } from '@krp/client-runtime'
import { receiptsIdentifiedHere, receiptsPeople, type IdentifiedCashier } from '@krp/resource-receipts-ui'
import {
  cashierRemembered,
  cellKey,
  dayBusyHint,
  isDayBusy,
  scheduleCashierDayClear,
  scheduleCashierDaySet,
  shownError,
  type CashierDirectory,
  type CellKind,
  type DeniedCell,
  type GridCell,
  type GridRow,
  type ShownError,
} from '@krp/schedule-ui'

import { useAppDispatch } from '../../app/hooks'

/**
 * Общее для окон раздела «График выхода»: справочник кассиров и вид запроса.
 *
 * Служба графика знает кассира только по идентификатору, а имена берёт приложение: из собственных кассиров службы Cashiers
 * (RP-CU3, вкладка «Кассиры»). Идентификатор — тот же, что у прежних «людей» справочника, поэтому составленный график сохраняется.
 */

/** Подпись строки, когда имя кассира справочник не назвал: слова и последние символы идентификатора, а не выдуманное имя. */
export const cashierFallbackName = (cashierId: string): string => `Кассир …${cashierId.slice(-6)}`

export function useCashierDirectory(): CashierDirectory {
  const directory = receiptsPeople.useQuery()
  const everyone = directory.data?.items ?? []
  const names = new Map(everyone.map((person) => [person.id.toLowerCase(), person.displayName]))
  const status: CashierDirectory['status'] = directory.isError ? 'unavailable' : directory.data === undefined ? 'loading' : 'ready'
  return {
    status,
    error: directory.isError ? errorView(directory.error) : null,
    people: everyone.filter((person) => !person.archived).map((person) => ({ id: person.id, name: person.displayName })),
    nameOf: (cashierId) => names.get(cashierId.toLowerCase()) ?? cashierFallbackName(cashierId),
  }
}

interface QueryLike {
  data?: unknown
  error?: unknown
  isError: boolean
  isFetching: boolean
}

function statusOfError(kind: string): QueryStatus {
  if (kind === 'unauthorized') return 'unauthorized'
  if (kind === 'forbidden') return 'forbidden'
  if (kind === 'unavailable' || kind === 'network' || kind === 'timeout') return 'unavailable'
  return 'error'
}

/**
 * Вид запроса для экрана: ошибка при уже полученных данных показывается ошибкой, а не старыми данными как актуальными (F06);
 * для запроса со сменой аргументов (период графика) в `data` передают `currentData` — данные именно этого периода.
 */
export function viewOf(result: QueryLike): QueryView {
  if (result.isError) {
    const error = errorView(result.error)
    return { status: statusOfError(error.kind), refreshing: false, error }
  }
  if (result.data === undefined) return { status: 'loading', refreshing: false, error: null }
  return { status: 'ready', refreshing: result.isFetching, error: null }
}

/** Кассир, у которого есть связи с источниками: нужно, чтобы узнать в нём вошедшего в кассу. */
interface PersonWithLinks {
  id: string
  archived: boolean
  links: readonly { resourceId: string; key: string; sourceCashierId: string | null }[]
}

/**
 * Кассиры графика, соответствующие тем, кто сейчас идентифицирован в кассе на этом устройстве, — тот же источник и то же
 * правило, что у вкладки «Статистика кассира» («Сейчас идентифицирован», `receiptsIdentifiedHere`): вошедший в источник кассир
 * узнаётся по ключу или по идентификатору кассира источника в связях собственного кассира.
 */
export function currentCashierIds(people: readonly PersonWithLinks[], identified: readonly IdentifiedCashier[]): string[] {
  return people
    .filter(
      (person) =>
        !person.archived &&
        person.links.some((link) =>
          identified.some(
            (item) =>
              item.resourceId.toLowerCase() === link.resourceId.toLowerCase() &&
              (item.cashierKey === link.key || (link.sourceCashierId !== null && link.sourceCashierId === item.sourceCashierId)),
          ),
        ),
    )
    .map((person) => person.id.toLowerCase())
}

/**
 * Кассир открытой смены, предложенный по умолчанию на экране «По кассирам»: тот же источник, что у «Сейчас идентифицирован»
 * (`currentCashierIds`), сопоставленный со справочником. `null` — смены нет или кассира в справочнике нет.
 */
export function useShiftCashierId(directory: CashierDirectory): string | null {
  const people = receiptsPeople.useQuery()
  const identified = receiptsIdentifiedHere.useQuery()
  const current = new Set(currentCashierIds(people.data?.items ?? [], identified.data ?? []))
  return directory.people.find((person) => current.has(person.id.toLowerCase()))?.id ?? null
}

export interface CellCommands {
  /** Клетки с неподтверждённой командой (`cellKey`). */
  pending: ReadonlySet<string>
  /** Отказ последней команды: уходит, когда человек нажал на клетку ещё раз. «День занят» сюда не попадает — см. `denied`. */
  error: ShownError | null
  /** «День занят» (известный клиенту или отказ службы): клетка вспыхивает красным, рядом подсказка; панели ошибки нет. */
  denied: DeniedCell | null
  dismissDenied: () => void
  /** Показать отказ, пришедший не из клетки (например, добавление в состав), и убрать прежний, когда начинается новое действие. */
  fail: (error: ShownError) => void
  clear: () => void
  /** Левый клик по клетке: «выходит» ставится, а если уже стоит — клетка очищается; занятый день запроса не отправляет. */
  toggle: (row: GridRow, cell: GridCell) => void
  /** Выбор статуса в меню клетки (`none` — очистить): одна команда; «выходит» на занятый день запроса не отправляет, а сразу показывает отказ у клетки. */
  setKind: (row: GridRow, cell: GridCell, kind: CellKind) => void
}

/** Что сказать, когда команда по клетке не прошла: по тому, что хотели поставить. */
const FAILURE_TITLES: Readonly<Record<CellKind, string>> = {
  work: 'Не удалось поставить кассира',
  day_off: 'Не удалось отметить выходной день',
  absent: 'Не удалось отметить, что кассир не выходит',
  none: 'Не удалось очистить клетку',
}

/**
 * Команды клеток графика, общие у таблицы объекта и экрана «По кассирам»: одна команда на выбор, клетка помечена
 * «сохраняется…» до ответа, отказ остаётся на экране целиком, пока человек не повторил действие.
 * Отказ «день занят» — не ошибка экрана: он рождается здесь и сразу превращается в вспышку клетки с подсказкой (`denied`), в `error`
 * он не попадает. Данные после любой команды
 * перечитываются самим кэшем запросов (теги), а оптимистичная правка при отказе откатывается.
 */
export function useCellCommands(): CellCommands {
  const dispatch = useAppDispatch()
  const [setCashierDay] = scheduleCashierDaySet.useMutation()
  const [clearCashierDay] = scheduleCashierDayClear.useMutation()
  const [pending, setPending] = useState<ReadonlySet<string>>(() => new Set())
  const [error, setError] = useState<ShownError | null>(null)
  const [denied, setDenied] = useState<DeniedCell | null>(null)

  const mark = (key: string, on: boolean) =>
    setPending((current) => {
      const next = new Set(current)
      if (on) next.add(key)
      else next.delete(key)
      return next
    })

  const deny = (key: string, text: string) => setDenied((current) => ({ key, text, seq: (current?.seq ?? 0) + 1 }))

  const setKind = (row: GridRow, cell: GridCell, kind: CellKind) => {
    if (cell.saving || kind === cell.kind) return
    setError(null)
    const { placeId, cashierId } = row
    const date = cell.day.day
    const key = cellKey(placeId, cashierId, date)
    if (kind === 'work' && cell.busyAt !== null) {
      deny(key, dayBusyHint(cell.busyAt))
      return
    }
    setDenied(null)
    if (kind !== 'none') dispatch(cashierRemembered(cashierId))
    mark(key, true)
    void (kind === 'none' ? clearCashierDay({ placeId, date, cashierId }) : setCashierDay({ placeId, date, cashierId, kind }))
      .unwrap()
      .catch((failure: unknown) => {
        if (isDayBusy(failure)) deny(key, errorView(failure).message)
        else setError(shownError(FAILURE_TITLES[kind], failure))
      })
      .finally(() => mark(key, false))
  }

  const toggle = (row: GridRow, cell: GridCell) => setKind(row, cell, cell.kind === 'work' ? 'none' : 'work')

  /** Стабильная ссылка: таблица подписывается на события документа, пока подсказка видна, и не должна переподписываться на каждый рендер. */
  const dismissDenied = useCallback(() => setDenied(null), [])
  const clear = () => {
    setError(null)
    setDenied(null)
  }

  return { pending, error, denied, dismissDenied, fail: setError, clear, toggle, setKind }
}
